import { useMemo, useCallback, useState, useEffect } from 'react'

import {
  useAccountId,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import { useChannelGroups } from '~publish/pages/Settings/ChannelGroups/hooks/useChannelGroups'
import type { ChannelGroup_ChannelGroup } from '~publish/pages/Settings/ChannelGroups/hooks/useChannelGroups'

interface UseChannelGroupsFilterParams {
  selectedChannelIds: string[]
  onChannelSelect: (channelIds: string[]) => void
  /** When provided, resets expanded state when transitioning to false (e.g. combobox closing) */
  open?: boolean
  /** Channels that can't be selected; group selection skips them. */
  disabledChannelIds?: Set<string>
}

interface UseChannelGroupsFilterReturn {
  isExpanded: boolean
  channelGroups: ChannelGroup_ChannelGroup[] | null
  filteredChannelGroups: ChannelGroup_ChannelGroup[]
  selectedChannelGroupIds: string[]
  /** Groups whose channels are all disabled, so they can't be selected. */
  disabledChannelGroupIds: string[]
  handleSelectGroup: (channelIds: string[], isSelected: boolean) => void
  setIsExpanded: (expanded: boolean) => void
}

/**
 * Hook that provides channel groups filtering functionality.
 * Consolidates logic for fetching channel groups, computing selected groups,
 * and handling group selection across different filter components.
 *
 * @param selectedChannelIds - Array of currently selected channel IDs
 * @param onChannelSelect - Callback to update selected channel IDs
 */
export function useChannelGroupsFilter({
  selectedChannelIds,
  onChannelSelect,
  open,
  disabledChannelIds,
}: UseChannelGroupsFilterParams): UseChannelGroupsFilterReturn {
  const accountId = useAccountId()
  const organizationId = useOrganizationId()
  const [isExpanded, setIsExpanded] = useState(false)

  // Reset expanded state when the parent container closes
  useEffect(() => {
    if (open === false) {
      setIsExpanded(false)
    }
  }, [open])

  const { channelGroups } = useChannelGroups({
    accountId: accountId ?? '',
    organizationId: organizationId ?? '',
  })

  // Filter out groups with no channels
  const filteredChannelGroups = useMemo(
    () =>
      (channelGroups ?? []).filter(
        (group) => (group.channels?.length ?? 0) > 0,
      ),
    [channelGroups],
  )

  // Compute which channel groups are fully selected based on the current filter
  const selectedChannelGroupIds = useMemo(() => {
    if (!channelGroups || channelGroups.length === 0) {
      return []
    }

    return [
      ...new Set(
        channelGroups
          .filter((group) => {
            // Disabled channels are never selectable, so judge the group by its
            // selectable channels only — otherwise a group containing one would
            // never read as fully selected.
            const groupChannelIds = (
              group.channels?.map((channel) => channel.id) ?? []
            ).filter((channelId) => !disabledChannelIds?.has(channelId))
            // A group is selected if all its selectable channels are in the filter
            return (
              groupChannelIds.length > 0 &&
              groupChannelIds.every((channelId) =>
                selectedChannelIds.includes(channelId),
              )
            )
          })
          .map((group): string => String(group.id)),
      ),
    ]
  }, [channelGroups, selectedChannelIds, disabledChannelIds])

  // Groups whose every channel is disabled — selecting them would be a no-op, so
  // consumers render them disabled rather than as dead chips.
  const disabledChannelGroupIds = useMemo(() => {
    if (!disabledChannelIds || !channelGroups) return []
    return channelGroups
      .filter((group) => {
        const ids = group.channels?.map((channel) => channel.id) ?? []
        return ids.length > 0 && ids.every((id) => disabledChannelIds.has(id))
      })
      .map((group): string => String(group.id))
  }, [channelGroups, disabledChannelIds])

  const handleSelectGroup = useCallback(
    (channelIds: string[], isSelected: boolean): void => {
      if (isSelected) {
        // Remove all group channels from the filter
        const newSelection = selectedChannelIds.filter(
          (id) => !channelIds.includes(id),
        )
        onChannelSelect(newSelection)
      } else {
        // Add the group's selectable channels to the filter (skipping disabled
        // ones, e.g. channels unsupported on this surface), avoiding duplicates.
        const selectableIds = channelIds.filter(
          (id) => !disabledChannelIds?.has(id),
        )
        const newSelection = [
          ...new Set([...selectedChannelIds, ...selectableIds]),
        ]
        onChannelSelect(newSelection)
      }
    },
    [selectedChannelIds, onChannelSelect, disabledChannelIds],
  )

  return {
    isExpanded,
    channelGroups,
    filteredChannelGroups,
    selectedChannelGroupIds,
    disabledChannelGroupIds,
    setIsExpanded,
    handleSelectGroup,
  }
}
